import { useCallback, useEffect, useState } from 'react';
import {
  FileAddOutlined,
  PlusCircleOutlined,
  InfoCircleOutlined,
  AlertOutlined,
  AppstoreAddOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import {
  ATitle,
  ASelect,
  AButton,
  OptionFooter,
  TreatmentProductsModal,
  TreatmentRetailProductsModal,
} from '../../../components';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { Col, Form, Row, Table } from 'antd';
import * as S from './styles';
import {
  IProcedure,
  IProcedureInsert,
  IClient,
  ITreatment,
  IPaymentMethod,
  IProfessional,
} from '../../../shared/interfaces';
import { currency } from '../../../shared/utils/functions';
import InsertEditProcedures from './InsertEditProcedure';
import Discount from './Discount';
import { schemaTreatmentCreate } from '../schemas/yup.treatment.schema';
import RemoveProcedures from './RemoveProcedure';
import ConfirmTreatment from '../ApprovalOrNot/Confirm';
import {
  getSelectClients,
  getSelectPaymentsMethods,
  getSelectProcedures,
  getSelectProfessionals,
  getTreatment,
  postTreatment,
} from '../../../http';
import api from '../../../services/api';
import { IRetailSaleProduct } from '../../../components/TreatmentRetailProductsModal';

const TratmentCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [type, setType] = useState<number>(0);
  const [code, setCode] = useState<number | null>(null);
  const [clients, setClients] = useState<IClient[]>([]);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [professional_id, setProfessional_id] = useState<string | null>(null);
  const [client_id, setClient_id] = useState<string | null>(null);
  const [procedures, setProcedures] = useState<IProcedure[]>([]);
  const [modalCreate, setModalCreate] = useState<boolean>(false);
  const [modalRemove, setModalRemove] = useState<boolean>(false);
  const [treatment, setTreatment] = useState<ITreatment | null>(null);
  const [proceduresInsert, setProceduresInsert] = useState<IProcedureInsert[]>(
    [],
  );
  const [procedureInsert, setProcedureInsert] =
    useState<IProcedureInsert | null>(null);
  const [type_discount, setType_Discount] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [value_discount, setValue_discount] = useState(0);
  const [total, setTotal] = useState<number>(0);
  const [totalFinal, setTotalFinal] = useState<number>(0);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [disabledField, setDisabledField] = useState<boolean>(false);
  const [modalConfirm, setModalConfirm] = useState<boolean>(false);
  const [paymentsMethods, setPaymentsMethods] = useState<IPaymentMethod[]>([]);
  const [approval, setApproval] = useState<boolean>(false);
  const [modalProducts, setModalProducts] = useState<boolean>(false);
  const [modalRetailProducts, setModalRetailProducts] =
    useState<boolean>(false);
  const [retailProducts, setRetailProducts] = useState<IRetailSaleProduct[]>(
    [],
  );
  const [linkedProductsCount, setLinkedProductsCount] = useState<number>(0);
  const [linkedProductsTotal, setLinkedProductsTotal] = useState<number>(0);

  const treatmentIdForProducts = id !== 'new' ? id : treatment?.id;
  const retailProductsTotal = retailProducts.reduce(
    (sum, item) => sum + item.total_price,
    0,
  );
  const combinedSaleTotal = total + linkedProductsTotal + retailProductsTotal;

  const listProfessionals = useCallback(async () => {
    try {
      const data = await getSelectProfessionals();
      setProfessionals(data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar os profissionais',
      });
    }
  }, [addToast]);

  const loadProcedures = useCallback(async () => {
    await getSelectProcedures('all')
      .then((data) => {
        setProcedures(data);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description:
            error.response.data.error ?? 'Erro ao listar os procedimentos.',
        });
      });
  }, []);

  const loadTreatment = useCallback(async () => {
    await getTreatment(id)
      .then((data) => {
        const { treatment, items, retail_items, linked_products } = data;
        setCode(treatment.code);
        setClient_id(treatment.client_id);
        setProfessional_id(treatment.professional_id);
        setType(treatment.type);
        setProceduresInsert(items);
        setRetailProducts(
          (retail_items ?? []).map((item) => ({
            id: item.id,
            product_id: item.product_id,
            quantity: Number(item.quantity || 0),
            unit_price: Number(item.unit_price || 0),
            total_price: Number(item.total_price || 0),
          })),
        );
        setLinkedProductsCount((linked_products ?? []).length);
        setLinkedProductsTotal(
          (linked_products ?? []).reduce(
            (sum, item) => sum + Number(item.total_price || 0),
            0,
          ),
        );
        setDisabledField(true);
      })
      .catch((error) => {
        setDisabledField(false);
        addToast({
          type: 'error',
          title: 'Erro',
          description: error.response.data.error ?? 'Erro ao consultar.',
        });
      });
  }, [id]);

  const loadClients = useCallback(async () => {
    await getSelectClients(1)
      .then((data) => {
        setClients(data);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${
            error.response.data.error ?? 'Erro ao listar os clientes.'
          }`,
        });
      });
  }, []);

  const listPaymentsMethods = useCallback(async () => {
    await getSelectPaymentsMethods()
      .then((data) => setPaymentsMethods(data))
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: 'Erro ao listar formas de pagamento',
        });
      });
  }, []);

  const loadLinkedProductsSummary = useCallback(async () => {
    if (!treatmentIdForProducts) {
      setLinkedProductsCount(0);
      setLinkedProductsTotal(0);
      return;
    }

    try {
      const response = await api.get(
        `/treatments-products/treatment/${treatmentIdForProducts}`,
      );
      const items = Array.isArray(response.data) ? response.data : [];

      setLinkedProductsCount(items.length);
      setLinkedProductsTotal(
        items.reduce(
          (sum: number, item: { total_price?: number }) =>
            sum + Number(item.total_price || 0),
          0,
        ),
      );
    } catch {
      setLinkedProductsCount(0);
      setLinkedProductsTotal(0);
    }
  }, [treatmentIdForProducts]);

  const loadTitle = useCallback(() => {
    let title = id !== 'new' ? 'Editar ' : 'Cadastrar ';
    title += type > 0 ? 'tratamento ' : 'orçamento ';
    title += code ? `Nº ${code}` : '';
    return title;
  }, [id, type, code]);

  const calculateTotal = useCallback(() => {
    let sum = proceduresInsert.reduce(
      (acc, current) => acc + +current.value * current.quantity,
      0,
    );

    const disct = proceduresInsert.reduce(
      (acc, current) => acc + current.discount * current.quantity,
      0,
    );
    sum -= disct;
    let discountValue = 0;
    if (discount) {
      const value_porcent = sum * (discount / 100);
      discountValue = type_discount === 0 ? value_porcent : discount;
    }
    setValue_discount(discountValue);
    setTotal(sum - discountValue);
    setTotalFinal(sum);
  }, [proceduresInsert, type_discount, discount]);

  const handleSubmit = useCallback(() => {
    setDisabled(true);
    schemaTreatmentCreate
      .validate({
        type,
        client_id,
        total: totalFinal,
        professional_id,
        items: proceduresInsert,
        discount: value_discount,
      })
      .then(async (validated: any) => {
        await postTreatment(validated)
          .then((data) => {
            setTreatment(data);
            addToast({
              type: 'success',
              title: 'Sucesso',
              description: `${
                type ? 'Tratamento' : 'Orçamento'
              } cadastrado com sucesso`,
            });
            if (type === 1) {
              listPaymentsMethods();
              setModalConfirm(true);
            } else {
              setTimeout(function () {
                navigate(-1);
              }, 1000);
            }
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response.data.error ?? 'Erro ao cadastrar.',
            });
            setDisabled(false);
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors,
        });
      })
      .finally(() => {
        setDisabled(false);
      });
  }, [
    type,
    client_id,
    professional_id,
    proceduresInsert,
    value_discount,
    discount,
    totalFinal,
  ]);

  const handleCreateInsert = useCallback((value?: IProcedureInsert) => {
    setProcedureInsert(value ?? null);
    setModalCreate(true);
  }, []);

  const handleRemoveInsert = useCallback((value: IProcedureInsert) => {
    setProcedureInsert(value);
    setModalRemove(true);
  }, []);

  const renderModalProcedureCreateEdit = () => {
    if (modalCreate)
      return (
        <InsertEditProcedures
          open={modalCreate}
          close={() => setModalCreate(false)}
          treatment_id={id}
          professional_id={professional_id}
          code={code}
          setProceduresInsert={setProceduresInsert}
          procedures={procedures}
          setProcedureInsert={setProcedureInsert}
          procedureInsert={procedureInsert}
        />
      );
  };

  const renderModalProcedureRemove = () => {
    if (modalRemove && procedureInsert)
      return (
        <RemoveProcedures
          open={modalRemove}
          close={() => setModalRemove(false)}
          code={code}
          procedureInsert={procedureInsert}
          setProceduresInsert={setProceduresInsert}
        />
      );
  };

  const closeModalConfirm = useCallback(() => {
    if (treatment?.type === 1 && !approval) {
      addToast({
        type: 'warning',
        title: 'Aprovação não concluída',
        description: 'Finalize os pagamentos para concluir.',
      });
      return;
    }
    setModalConfirm(false);
  }, [approval, treatment]);

  const renderModalConfirm = () => {
    if (modalConfirm) {
      const nestedTreatment =
        (treatment as any)?.treatment ?? (treatment as any)?.data ?? treatment;
      const resolvedTreatmentId =
        (nestedTreatment as any)?.id ??
        (nestedTreatment as any)?.treatment_id ??
        (id !== 'new' ? id : undefined);
      return (
        <ConfirmTreatment
          open={modalConfirm}
          close={closeModalConfirm}
          treatment={treatment}
          paymentsMethods={paymentsMethods}
          setApproval={setApproval}
          retailProducts={retailProducts}
          treatmentTotal={total}
          totalAmount={combinedSaleTotal}
          treatment_id={resolvedTreatmentId}
        />
      );
    }
  };

  useEffect(() => {
    calculateTotal();
  }, [proceduresInsert, type_discount, discount]);

  useEffect(() => {
    loadClients();
    listProfessionals();
    loadProcedures();
  }, []);

  useEffect(() => {
    if (id !== 'new') {
      loadTreatment();
    }
  }, [id]);

  useEffect(() => {
    if (id === 'new') {
      loadLinkedProductsSummary();
    }
  }, [loadLinkedProductsSummary, id]);

  return (
    <>
      <S.HeaderWrapper>
        <ATitle icon={<FileAddOutlined />}>{loadTitle()}</ATitle>
      </S.HeaderWrapper>

      {/* Seção: Seleção de Tipo, Cliente e Profissional */}
      <S.SectionWrapper>
        <S.StyledCard>
          <S.FormSectionTitle>
            <span className="icon">👤</span>
            Informações Básicas
          </S.FormSectionTitle>

          <S.InstructionBox>
            <InfoCircleOutlined className="icon" />
            <p className="text">
              Selecione o tipo de procedimento (Tratamento ou Orçamento), o
              paciente e o profissional responsável.
            </p>
          </S.InstructionBox>

          <Form layout="vertical" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  label="Tipo de Procedimento"
                  required
                  tooltip="Selecione se é um tratamento completo ou orçamento"
                >
                  <ASelect
                    value={type}
                    onChange={(e) => setType(e)}
                    options={[
                      { label: '💊 Tratamento', value: 1 },
                      { label: '📋 Orçamento', value: 0 },
                    ]}
                    disabled={disabledField}
                  />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item
                  label="Paciente"
                  required
                  tooltip="Selecione o paciente responsável"
                >
                  <ASelect
                    showSearch
                    placeholder="Selecione o paciente"
                    optionFilterProp="children"
                    onChange={(e) => setClient_id(e)}
                    filterOption={(input, option: any) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    value={client_id}
                    allowClear
                    options={clients.map((client) => ({
                      label: client.corporate_name,
                      value: client.id,
                    }))}
                    disabled={disabledField}
                  />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item
                  label="Profissional"
                  required
                  tooltip="Selecione o profissional responsável"
                >
                  <ASelect
                    showSearch
                    placeholder="Selecione o profissional"
                    optionFilterProp="children"
                    onChange={(e) => setProfessional_id(e)}
                    filterOption={(input, option: any) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    value={professional_id}
                    allowClear
                    options={professionals.map((prof) => ({
                      label: prof.name,
                      value: prof.id,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </S.StyledCard>
      </S.SectionWrapper>

      {/* Seção: Procedimentos */}
      <S.SectionWrapper>
        <S.StyledCard>
          <S.FormSectionTitle>
            <span className="icon">⚕️</span>
            Procedimentos Inclusos
          </S.FormSectionTitle>

          <S.InstructionBox>
            <InfoCircleOutlined className="icon" />
            <p className="text">
              Adicione os procedimentos que serão executados. Click no botão
              abaixo para inserir um novo procedimento ou editar os existentes.
            </p>
          </S.InstructionBox>

          {proceduresInsert.length === 0 ? (
            <S.EmptyStateBox>
              <div className="icon">📋</div>
              <p className="text">Nenhum procedimento adicionado ainda</p>
              <AButton
                type="primary"
                onClick={() => handleCreateInsert()}
                icon={<PlusCircleOutlined />}
              >
                Inserir Primeiro Procedimento
              </AButton>
            </S.EmptyStateBox>
          ) : (
            <>
              <Row justify="end" style={{ marginBottom: 16 }}>
                <AButton
                  type="primary"
                  onClick={() => handleCreateInsert()}
                  icon={<PlusCircleOutlined />}
                >
                  Inserir Procedimento
                </AButton>
              </Row>

              <S.TableContainer>
                <Table
                  style={{ width: '100%' }}
                  scroll={{ x: true, y: 270 }}
                  columns={[
                    {
                      title: '#',
                      width: 75,
                      render: (value, record, index) => (
                        <strong>{index + 1}</strong>
                      ),
                    },
                    {
                      title: 'Procedimento',
                      dataIndex: 'procedure_id',
                      width: 200,
                      render: (value: string) => (
                        <>
                          {procedures.find((x) => x.id === value)?.description}
                        </>
                      ),
                    },
                    {
                      title: 'Qtd.',
                      dataIndex: 'quantity',
                      width: 75,
                      align: 'center' as const,
                    },
                    {
                      title: 'Valor UN.',
                      width: 130,
                      render: (cell: IProcedureInsert) => (
                        <strong>
                          {currency(+cell.value - +cell.discount)}
                        </strong>
                      ),
                    },
                    {
                      title: 'Desconto',
                      width: 130,
                      render: (cell: IProcedureInsert) => (
                        <>{currency(cell.discount * cell.quantity)}</>
                      ),
                    },
                    {
                      title: 'Total',
                      width: 130,
                      render: (cell: IProcedureInsert) => (
                        <strong>
                          {currency(
                            cell.value * cell.quantity -
                              cell.discount * cell.quantity,
                          )}
                        </strong>
                      ),
                    },
                    {
                      title: 'Ações',
                      width: 180,
                      render: (cell: IProcedureInsert) => (
                        <Row gutter={8}>
                          <Col>
                            <AButton
                              type="primary"
                              size="small"
                              onClick={() => handleCreateInsert(cell)}
                            >
                              Editar
                            </AButton>
                          </Col>
                          <Col>
                            <AButton
                              type="default"
                              danger
                              size="small"
                              onClick={() => handleRemoveInsert(cell)}
                            >
                              Remover
                            </AButton>
                          </Col>
                        </Row>
                      ),
                    },
                  ]}
                  dataSource={proceduresInsert}
                  pagination={false}
                  bordered
                  size="small"
                />
              </S.TableContainer>

              <S.SummaryBox>
                <div className="summary-row">
                  <span className="label">Subtotal dos procedimentos:</span>
                  <span className="value">{currency(totalFinal)}</span>
                </div>
                {value_discount > 0 && (
                  <div className="summary-row">
                    <span className="label">Desconto total:</span>
                    <span className="value" style={{ color: '#fff' }}>
                      -{currency(value_discount)}
                    </span>
                  </div>
                )}
                <div className="summary-row total-final">
                  <span className="label">Total a pagar:</span>
                  <span className="value">{currency(total)}</span>
                </div>
              </S.SummaryBox>

              {/* Componente de Desconto */}
              <div style={{ marginTop: 24 }}>
                <Discount
                  code={code}
                  type_discount={type_discount}
                  setType_Discount={setType_Discount}
                  total={total}
                  setDiscount={setDiscount}
                  discount={discount}
                />
              </div>
            </>
          )}
        </S.StyledCard>
      </S.SectionWrapper>

      {code && (
        <S.SectionWrapper>
          <S.InfoAlert>
            <AlertOutlined className="icon" />
            <p className="text">
              <strong>Registro salvo:</strong> Este{' '}
              {type ? 'tratamento' : 'orçamento'} foi salvo com sucesso (Nº{' '}
              {code}). Você pode visualizá-lo em detalhes ou imprimi-lo usando a
              opção de impressão.
            </p>
          </S.InfoAlert>
        </S.SectionWrapper>
      )}

      {type === 1 && (
        <S.SectionWrapper>
          <S.StyledCard>
            <S.FormSectionTitle>
              <span className="icon">📦</span>
              Venda Híbrida de Produtos
            </S.FormSectionTitle>

            <S.InstructionBox>
              <InfoCircleOutlined className="icon" />
              <p className="text">
                Use o modelo híbrido: produtos clinicamente ligados ao
                tratamento ficam vinculados ao protocolo, enquanto itens de
                revenda entram como produtos avulsos da venda.
              </p>
            </S.InstructionBox>

            <S.SalesModeGrid>
              <S.SalesModeCard>
                <div className="eyebrow">Produtos vinculados</div>
                <h4>Materiais vendidos junto com o tratamento</h4>
                <p>
                  Ideal para kits, materiais clínicos ou itens que fazem parte
                  da entrega terapêutica. A baixa de estoque segue o vínculo do
                  tratamento.
                </p>
                <div className="metaRow">
                  <span>{linkedProductsCount} produto(s) vinculados</span>
                  <strong>{currency(linkedProductsTotal)}</strong>
                </div>
                <AButton
                  type="primary"
                  icon={<AppstoreAddOutlined />}
                  onClick={() => setModalProducts(true)}
                  disabled={!treatmentIdForProducts}
                >
                  Gerenciar vinculados
                </AButton>
                {!treatmentIdForProducts && (
                  <div className="hint">
                    Salve o tratamento para habilitar o vínculo clínico dos
                    produtos.
                  </div>
                )}
              </S.SalesModeCard>

              <S.SalesModeCard>
                <div className="eyebrow">Produtos avulsos</div>
                <h4>Itens vendidos separadamente no atendimento</h4>
                <p>
                  Use para escovas, clareadores, enxaguantes ou qualquer produto
                  comercializado fora do protocolo clínico principal.
                </p>
                <div className="metaRow">
                  <span>{retailProducts.length} item(ns) na venda avulsa</span>
                  <strong>{currency(retailProductsTotal)}</strong>
                </div>
                <AButton
                  type="default"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => setModalRetailProducts(true)}
                >
                  Montar venda avulsa
                </AButton>
                <div className="hint">
                  Essa área organiza a revenda no frontend e separa visualmente
                  o que é clínico do que é comercial.
                </div>
              </S.SalesModeCard>
            </S.SalesModeGrid>

            <S.SummaryBox>
              <div className="summary-row">
                <span className="label">Tratamento após descontos:</span>
                <span className="value">{currency(total)}</span>
              </div>
              <div className="summary-row">
                <span className="label">
                  Produtos vinculados ao tratamento:
                </span>
                <span className="value">{currency(linkedProductsTotal)}</span>
              </div>
              <div className="summary-row">
                <span className="label">Produtos avulsos da venda:</span>
                <span className="value">{currency(retailProductsTotal)}</span>
              </div>
              <div className="summary-row total-final">
                <span className="label">Total comercial estimado:</span>
                <span className="value">{currency(combinedSaleTotal)}</span>
              </div>
            </S.SummaryBox>
          </S.StyledCard>
        </S.SectionWrapper>
      )}
      <S.SectionWrapper>
        <OptionFooter
          width="100%"
          onClickCancel={() => navigate(-1)}
          onClickOk={handleSubmit}
          disabled={disabled}
          showOk={!code}
        />
      </S.SectionWrapper>

      {renderModalProcedureCreateEdit()}
      {renderModalProcedureRemove()}
      {renderModalConfirm()}
      {treatmentIdForProducts && (
        <TreatmentProductsModal
          open={modalProducts}
          onClose={() => {
            setModalProducts(false);
            loadLinkedProductsSummary();
          }}
          treatmentId={treatmentIdForProducts}
        />
      )}
      <TreatmentRetailProductsModal
        open={modalRetailProducts}
        onClose={() => setModalRetailProducts(false)}
        items={retailProducts}
        setItems={setRetailProducts}
      />
    </>
  );
};

export default TratmentCreateEdit;
